import { indexedDbBackend, type RecordingBackend, type RecordingHeader } from "./recording-store";

/**
 * The write side of the recording's shadow on disk.
 *
 * Everything here is fire-and-forget by construction: `append` is synchronous, returns nothing and
 * cannot throw. The recording path calls it from `ondataavailable` and carries on, because a disk
 * that is full or slow must cost the author nothing while they are still recording.
 */
export interface RecordingPersistence {
	/** Start a new record. Resets the sequence, so a second recording does not continue the first. */
	begin(header: RecordingHeader): void;
	/** Store one chunk. Never throws, never awaited. */
	append(blob: Blob, elapsedMs: number): void;
	/** No more chunks are coming. The record stays exactly as it is, for recovery to find. */
	finish(): void;
	/**
	 * Take responsibility for a record already on disk — one rescued at the last start. Nothing is
	 * written; the queue only needs to know which record it may later discard, and that a copy of
	 * this take exists.
	 */
	adopt(videoId: string): void;
	/** Whether a playable copy of the current recording is on disk. */
	hasDurableRecord(): boolean;
	/** Remove a record for good, on the same chain as the writes so it cannot overtake one. */
	discard(videoId: string): void;
}

/**
 * More outstanding writes than this means the disk is not keeping up with one blob a second, and a
 * backlog of that shape does not clear — it grows until the tab does. The record is truncated at
 * what has landed, which is still a recoverable recording.
 */
const MAX_OUTSTANDING_WRITES = 8;

/**
 * Build a persistence queue over a storage backend.
 *
 * @param backend - Where records are written. The tests pass an in-memory one.
 * @returns The queue, whose whole surface is safe to call from a recording in progress.
 */
export function createRecordingPersistence(backend: RecordingBackend): RecordingPersistence {
	let videoId: string | null = null;
	let seq = 0;
	let outstanding = 0;
	let isOpen = false;
	let isTruncated = false;
	let hasFirstChunk = false;
	let tail: Promise<unknown> = Promise.resolve();
	/**
	 * Which record the queue is on. Every queued write carries the value it was made under, so a
	 * write left over from the last recording cannot report a failure against this one — which
	 * would truncate, and with no first chunk stored yet, delete a recording that is running fine.
	 */
	let generation = 0;

	/**
	 * Stop writing this record for good.
	 *
	 * Writes already queued are dropped too, and that is the point: letting them through after one
	 * failed would leave a hole in the middle of the record, and a hole is indistinguishable from
	 * an end — the bytes after it decode against a cluster that never arrived.
	 */
	function truncate(forGeneration: number) {
		if (forGeneration !== generation || isTruncated) return;
		isTruncated = true;
		// Without the first chunk there is no EBML header and no codec initialisation, so nothing
		// that follows can be played. The record goes rather than being offered.
		if (!hasFirstChunk && videoId) void backend.remove(videoId);
	}

	function enqueue(work: () => Promise<boolean>) {
		const forGeneration = generation;
		outstanding++;
		tail = tail.then(async () => {
			try {
				if (forGeneration !== generation || isTruncated) return;
				if (!(await work())) truncate(forGeneration);
			} catch {
				truncate(forGeneration);
			} finally {
				if (forGeneration === generation) outstanding--;
			}
		});
	}

	return {
		begin(header) {
			generation++;
			videoId = header.videoId;
			seq = 0;
			outstanding = 0;
			isOpen = true;
			isTruncated = false;
			hasFirstChunk = false;
			enqueue(() => backend.putHeader(header));
		},

		append(blob, elapsedMs) {
			if (!isOpen || isTruncated || !videoId) return;
			if (outstanding >= MAX_OUTSTANDING_WRITES) {
				truncate(generation);
				return;
			}
			const row = { videoId, seq: seq++, blob, elapsedMs };
			const forGeneration = generation;
			enqueue(async () => {
				const isStored = await backend.putChunk(row);
				if (isStored && row.seq === 0 && forGeneration === generation) hasFirstChunk = true;
				return isStored;
			});
		},

		finish() {
			isOpen = false;
		},

		adopt(id) {
			generation++;
			videoId = id;
			seq = 0;
			outstanding = 0;
			isOpen = false;
			isTruncated = false;
			hasFirstChunk = true;
		},

		hasDurableRecord() {
			return hasFirstChunk && !isTruncated;
		},

		discard(id) {
			if (id === videoId) {
				isOpen = false;
				isTruncated = true;
				hasFirstChunk = false;
			}
			tail = tail.then(() => backend.remove(id)).catch(() => undefined);
		},
	};
}

/** The one queue the recorder writes through. */
export const persistence = createRecordingPersistence(indexedDbBackend);
